import authService from '@/authentication/authentication.service';

export class BaseApiClient {
  public async send<T = void>(
    method: HttpVerb,
    url: string,
    data?: any,
    optionOverrides?: Partial<ApiOptions>,
  ): Promise<T> {
    const headers: HeadersInit = {};

    const defaultOptions: ApiOptions = { responseType: undefined, useAuth: true };
    const options: ApiOptions = { ...defaultOptions, ...optionOverrides };

    if (options.useAuth) {
      const token = await authService.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (method !== 'GET' && method !== 'HEAD') {
      if (data instanceof FormData) {
        fetchOptions.body = data;
      } else {
        headers['Content-Type'] = 'application/json';
        fetchOptions.body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(url, fetchOptions);
      if (!response.ok) {
        return this.handleError(response);
      }

      const contentLength = response.headers.get('Content-Length');
      if (contentLength === '0' || !response.body) {
        return undefined as T;
      }

      const contentType = response.headers.get('Content-Type')?.toLowerCase() || '';
      const effectiveResponseType = options.responseType || this.inferResponseType(contentType);

      switch (effectiveResponseType) {
        case 'json':
          const text = await response.text();
          return text ? (JSON.parse(text) as T) : (undefined as T);
        case 'blob':
          return (await response.blob()) as T;
        case 'arraybuffer':
          return (await response.arrayBuffer()) as T;
        case 'text':
        default:
          return (await response.text()) as T; // Default to text for unrecognized types
      }
    } catch (err) {
      return Promise.reject({
        status: -1,
        message: (err as Error).message,
      } as ApiError);
    }
  }

  private inferResponseType(contentType: string): 'text' | 'json' | 'blob' | 'arraybuffer' {
    if (contentType.includes('application/json')) {
      return 'json';
    }
    if (contentType.includes('text/')) {
      return 'text';
    }
    if (contentType.includes('application/octet-stream') || contentType.includes('application/pdf')) {
      return 'blob';
    }
    if (contentType.includes('arraybuffer')) {
      return 'arraybuffer';
    }
    return 'text'; // Fallback to text for unknown types
  }

  private async handleError(response: Response): Promise<never> {
    const message = await response.text();
    return Promise.reject({
      status: response.status,
      message: message || `Request failed with status ${response.statusText}`,
    } as ApiError);
  }
}

export const api = new BaseApiClient();

export interface ApiError {
  status: number;
  message: string;
}

export interface ApiOptions {
  responseType?: 'text' | 'json' | 'blob' | 'arraybuffer';
  useAuth: boolean;
}

export type HttpVerb = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';
